import { useMutation, useQueryClient } from 'react-query';
import request from '../../../helpers/request';
import { useDispatch } from 'react-redux';
import {
  createInformationsKeys,
  fetchInfoById,
} from '../queries/useInformations';
import { setSelectedInfo } from '../store/actions';
import useFilterForQuery from '../hooks/useFilterForQuery';
import useAddInfoAttachments from './useAddInfoAttachments';
import useAddHeaderImg from './useAddHeaderImg';
import useDeleteHeaderImg from './useDeleteHeaderImg';

const addInfo = async (values) => {
  const data = await request.post(
    `/Information`,
    values,
    'Не удалось создать информирование'
  );

  return data;
};

const changeInfo = async ({ infoId, values }) => {
  const data = await request.put(
    `/Information/${infoId}`,
    values,
    'Не удалось изменить данные информирования'
  );

  return data;
};

const useInfoMutations = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const queryParams = useFilterForQuery();

  const { addAttachments } = useAddInfoAttachments();
  const { addAsync: addImgHeader } = useAddHeaderImg();
  const { deleteHeaderImg } = useDeleteHeaderImg();

  const changeInfoMutation = useMutation(
    ({ infoId, values }) => changeInfo({ infoId, values }),
    {
      onSuccess: async (data, { infoId, files, headerImgData }) => {
        if (data?.result === 'Ok') {
          const { hasImg, isDeleted, value: imgData } = headerImgData || {};

          const hasFiles = files.length > 0;
          const hasImgData = Boolean(imgData);
          const shouldDeleteHeaderImg = Boolean(
            !imgData && isDeleted && hasImg
          );

          const promises = [
            hasFiles ? addAttachments(infoId, files) : null,
            hasImgData ? addImgHeader({ infoId, data: imgData }) : null,
            shouldDeleteHeaderImg ? deleteHeaderImg({ infoId }) : null,
          ].filter(Boolean);

          if (promises.length) {
            await Promise.allSettled(promises);
          }

          const updatedInfoArr = await fetchInfoById({
            infoId,
            queryClient,
          });

          if (updatedInfoArr?.length === 1) {
            const updatedInfo = updatedInfoArr[0];

            dispatch(setSelectedInfo(updatedInfo));

            queryClient.setQueryData(
              createInformationsKeys(queryParams),
              (oldData) =>
                oldData.map((oldInfo) => {
                  if (oldInfo.id === updatedInfo.id) {
                    return updatedInfo;
                  }

                  return oldInfo;
                })
            );
          }
        }
      },
    }
  );

  const addInfoMutation = useMutation(({ values }) => addInfo(values), {
    onSuccess: async (data, { files, headerImgData }) => {
      if (data?.result === 'Ok') {
        const promises = [
          files.length > 0 ? addAttachments(data.id, files) : null,
          headerImgData?.value
            ? addImgHeader({ infoId: data.id, data: headerImgData.value })
            : null,
        ].filter(Boolean);

        if (promises.length > 0) {
          await Promise.allSettled(promises);
        }

        const updatedInfoArr = await fetchInfoById({
          infoId: data.id,
          queryClient,
        });

        if (updatedInfoArr?.length === 1) {
          const updatedInfo = updatedInfoArr[0];

          dispatch(setSelectedInfo(updatedInfo));

          queryClient.setQueryData(
            createInformationsKeys(queryParams),
            (oldData) => [updatedInfo, ...oldData]
          );
        }
      }
    },
  });

  return {
    changeInfo: changeInfoMutation.mutateAsync,
    addInfo: addInfoMutation.mutateAsync,
    loading: changeInfoMutation.isLoading || addInfoMutation.isLoading,
  };
};

export default useInfoMutations;
